// Universal contact CTA. Captures name + email + phone, then lets the user pick:
// • Continue on WhatsApp (instant reply), green button
// • Send request (we email you back), accent button
// Both paths store the lead in the DB via the whatsapp-lead edge function so
// nothing is ever lost, even if the user abandons WhatsApp Web after redirect.
import { useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { bookingFieldCopy, buildTourWhatsAppMessage } from "@/lib/tourWhatsAppMessage";
import { toast } from "sonner";
import { logAndOpenWhatsApp, logEmailRequest } from "@/lib/whatsappLead";

const WA_PHONE = "393880919006";

interface Props {
  /** Original pre-filled WhatsApp/email message body. */
  baseMessage: string;
  /** Tracking source, must match the previous data-wa-source value. */
  source: string;
  /** Visual classes (e.g. "lp-cta lp-cta--lg"). */
  className?: string;
  /** Inline styles passed through to the button. */
  style?: CSSProperties;
  /** Button label / children. */
  children: ReactNode;
  /** UI language, drives modal copy. */
  uiLang?: "en" | "de" | "pt" | "it" | "fr" | "es";
  /** Lets a parent open the form after closing another dialog. */
  modalOpen?: boolean;
  onModalOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
  bookingDetails?: boolean;
}

const COPY = {
  en: {
    title: "Get in touch",
    sub: "WhatsApp is fastest. No WhatsApp? Send a request and we reply by email within 24h. No spam, no newsletter.",
    name: "Your name",
    email: "Email",
    phone: "Phone (with country code)",
    note: "Anything we should know? (optional)",
    submitWa: "💬 Continue on WhatsApp",
    submitEmail: "✉ Send request (reply by email)",
    sending: "Sending…",
    cancel: "Cancel",
    nameErr: "Please enter your name",
    emailErr: "Please enter a valid email",
    phoneErr: "Please enter a valid phone number",
    sentOk: "Thanks! We received your request and will reply within 24h.",
    sentErr: "Something went wrong. Please try WhatsApp or retry.",
  },
  de: {
    title: "Kontakt aufnehmen",
    sub: "WhatsApp ist am schnellsten. Kein WhatsApp? Sende eine Anfrage, wir antworten per E-Mail innerhalb 24h.",
    name: "Ihr Name",
    email: "E-Mail",
    phone: "Telefon (mit Vorwahl)",
    note: "Etwas, das wir wissen sollten? (optional)",
    submitWa: "💬 Weiter auf WhatsApp",
    submitEmail: "✉ Anfrage senden (Antwort per E-Mail)",
    sending: "Senden…",
    cancel: "Abbrechen",
    nameErr: "Bitte Namen eingeben",
    emailErr: "Bitte gültige E-Mail eingeben",
    phoneErr: "Bitte gültige Telefonnummer eingeben",
    sentOk: "Danke! Wir haben Ihre Anfrage erhalten und antworten innerhalb 24h.",
    sentErr: "Etwas ist schiefgelaufen. Bitte WhatsApp nutzen oder erneut versuchen.",
  },
  pt: {
    title: "Entre em contacto",
    sub: "WhatsApp é mais rápido. Sem WhatsApp? Envie um pedido e respondemos por email em 24h.",
    name: "O seu nome",
    email: "Email",
    phone: "Telefone (com indicativo)",
    note: "Algo que devemos saber? (opcional)",
    submitWa: "💬 Continuar no WhatsApp",
    submitEmail: "✉ Enviar pedido (resposta por email)",
    sending: "A enviar…",
    cancel: "Cancelar",
    nameErr: "Indique o seu nome",
    emailErr: "Indique um email válido",
    phoneErr: "Indique um número válido",
    sentOk: "Obrigado! Recebemos o seu pedido e respondemos em 24h.",
    sentErr: "Algo correu mal. Tente WhatsApp ou tente de novo.",
  },
  it: {
    title: "Contattaci",
    sub: "WhatsApp è il più rapido. Niente WhatsApp? Invia una richiesta e ti rispondiamo via email entro 24h. No spam.",
    name: "Il tuo nome",
    email: "Email",
    phone: "Telefono (con prefisso)",
    note: "Qualcosa che dovremmo sapere? (opzionale)",
    submitWa: "💬 Continua su WhatsApp",
    submitEmail: "✉ Invia richiesta (risposta via email)",
    sending: "Invio…",
    cancel: "Annulla",
    nameErr: "Inserisci il nome",
    emailErr: "Inserisci un'email valida",
    phoneErr: "Inserisci un numero valido",
    sentOk: "Grazie! Abbiamo ricevuto la richiesta e rispondiamo entro 24h.",
    sentErr: "Qualcosa è andato storto. Prova WhatsApp o riprova.",
  },
  fr: {
    title: "Contactez-nous",
    sub: "WhatsApp est le plus rapide. Pas de WhatsApp ? Envoyez une demande, réponse par email sous 24h.",
    name: "Votre nom",
    email: "Email",
    phone: "Téléphone (avec indicatif)",
    note: "Quelque chose à nous dire ? (optionnel)",
    submitWa: "💬 Continuer sur WhatsApp",
    submitEmail: "✉ Envoyer la demande (réponse par email)",
    sending: "Envoi…",
    cancel: "Annuler",
    nameErr: "Indiquez votre nom",
    emailErr: "Indiquez un email valide",
    phoneErr: "Indiquez un numéro valide",
    sentOk: "Merci ! Nous avons reçu votre demande, réponse sous 24h.",
    sentErr: "Une erreur est survenue. Essayez WhatsApp ou réessayez.",
  },
  es: {
    title: "Contáctanos",
    sub: "WhatsApp es lo más rápido. ¿Sin WhatsApp? Envía una solicitud y respondemos por email en 24h.",
    name: "Tu nombre",
    email: "Email",
    phone: "Teléfono (con prefijo)",
    note: "¿Algo que debamos saber? (opcional)",
    submitWa: "💬 Continuar en WhatsApp",
    submitEmail: "✉ Enviar solicitud (respuesta por email)",
    sending: "Enviando…",
    cancel: "Cancelar",
    nameErr: "Indica tu nombre",
    emailErr: "Indica un email válido",
    phoneErr: "Indica un número válido",
    sentOk: "¡Gracias! Recibimos tu solicitud y respondemos en 24h.",
    sentErr: "Algo salió mal. Prueba WhatsApp o reinténtalo.",
  },
} as const;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LeadCaptureCTA = ({
  baseMessage,
  source,
  className,
  style,
  children,
  uiLang = "en",
  modalOpen,
  onModalOpenChange,
  hideTrigger = false,
  bookingDetails = false,
}: Props) => {
  const t = COPY[uiLang] ?? COPY.en;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = modalOpen ?? internalOpen;
  const setOpen = (next: boolean) => {
    if (modalOpen === undefined) setInternalOpen(next);
    onModalOpenChange?.(next);
  };
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [date, setDate] = useState("");
  const [participants, setParticipants] = useState("2");
  const bookingCopy = bookingFieldCopy[uiLang] ?? bookingFieldCopy.en;
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | "wa" | "email">(null);

  const validate = () => {
    if (!name.trim()) { setErr(t.nameErr); return null; }
    if ((!bookingDetails || email.trim()) && !EMAIL_RE.test(email.trim())) { setErr(t.emailErr); return null; }
    if (bookingDetails && (!date || !Number.isInteger(Number(participants)) || Number(participants) < 1)) return null;
    const cleanPhone = phone.replace(/[^\d+]/g, "");
    if (cleanPhone.length < 7) { setErr(t.phoneErr); return null; }
    return { cleanName: name.trim(), cleanEmail: email.trim(), cleanPhone };
  };

  const buildPersonalised = (cleanName: string, cleanEmail: string, cleanPhone: string) =>
    bookingDetails ? `${buildTourWhatsAppMessage(baseMessage, { name: cleanName, date, participants, message: note }, uiLang)}\n📱 ${cleanPhone}${cleanEmail ? `\n✉ ${cleanEmail}` : ""}` : `${baseMessage}
${cleanName}
✉ ${cleanEmail}
📱 ${cleanPhone}${note.trim() ? `\n\n${note.trim()}` : ""}`;

  const submitWhatsApp = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    const ok = validate();
    if (!ok) return;
    setBusy("wa");
    await logAndOpenWhatsApp(WA_PHONE, {
      source,
      message: buildPersonalised(ok.cleanName, ok.cleanEmail, ok.cleanPhone),
      language: uiLang,
      name: ok.cleanName,
      email: ok.cleanEmail,
      phone: ok.cleanPhone,
      context: {
        name: ok.cleanName,
        email: ok.cleanEmail,
        phone: ok.cleanPhone,
        note: note.trim() || null,
        baseMessage,
        ...(bookingDetails ? { preferredDate: date, participants } : {}),
      },
    });
    setBusy(null);
    setOpen(false);
    resetForm();
  };

  const submitEmail = async () => {
    setErr(null);
    const ok = validate();
    if (!ok) return;
    setBusy("email");
    const result = await logEmailRequest({
      source: `${source}__email`,
      message: buildPersonalised(ok.cleanName, ok.cleanEmail, ok.cleanPhone),
      language: uiLang,
      name: ok.cleanName,
      email: ok.cleanEmail,
      phone: ok.cleanPhone,
      consent: true,
      context: {
        name: ok.cleanName,
        email: ok.cleanEmail,
        phone: ok.cleanPhone,
        note: note.trim() || null,
        baseMessage,
      },
    });
    setBusy(null);
    if (result.ok) {
      toast.success(t.sentOk);
      setOpen(false);
      resetForm();
    } else {
      setErr(t.sentErr);
    }
  };

  const resetForm = () => {
    setName(""); setEmail(""); setPhone(""); setNote("");
    setDate(""); setParticipants("2");
  };

  return (
    <>
      {!hideTrigger && (
        <Button
          type="button"
          onClick={() => setOpen(true)}
          data-wa-capture={source}
          className={className}
          style={style}
        >
          {children}
        </Button>
      )}

      {open && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget && !busy) setOpen(false); }}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 16, zIndex: 9999,
          }}
        >
          <form
            onSubmit={submitWhatsApp}
            style={{
              background: "#fff", borderRadius: 16, padding: 24,
              width: "100%", maxWidth: 460, boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              fontFamily: "inherit", color: "#111",
              maxHeight: "92vh", overflowY: "auto",
            }}
          >
            <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{t.title}</h3>
            {!bookingDetails && <p style={{ margin: "6px 0 16px", fontSize: 13, color: "#555", lineHeight: 1.4 }}>{t.sub}</p>}

            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{t.name} *</label>
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              autoFocus required maxLength={80}
              style={{ width: "100%", padding: "10px 12px", border: "1.5px solid #ddd", borderRadius: 8, fontSize: 15, marginBottom: 10 }}
            />

            {!bookingDetails && <><label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{t.email} *</label>
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              required maxLength={120} placeholder="you@example.com" inputMode="email" autoComplete="email"
              style={{ width: "100%", padding: "10px 12px", border: "1.5px solid #ddd", borderRadius: 8, fontSize: 15, marginBottom: 10 }}
            /></>}

            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{t.phone} *</label>
            <input
              type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              required inputMode="tel" placeholder="+1 555 123 4567" maxLength={30} autoComplete="tel"
              style={{ width: "100%", padding: "10px 12px", border: "1.5px solid #ddd", borderRadius: 8, fontSize: 15, marginBottom: 10 }}
            />

            {bookingDetails && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <label className="text-sm font-semibold min-w-0">{bookingCopy.date} *
                <Input aria-label={bookingCopy.date} type="date" required value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full min-w-0" />
              </label>
              <label className="text-sm font-semibold">{bookingCopy.participants} *
                <Input aria-label={bookingCopy.participants} type="number" min={1} step={1} required value={participants} onChange={(e) => setParticipants(e.target.value)} className="mt-1" />
              </label>
            </div>}
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{bookingDetails ? bookingCopy.message : t.note}</label>
            <textarea
              value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={bookingDetails ? baseMessage : undefined}
              rows={2} maxLength={500}
              style={{ width: "100%", padding: "10px 12px", border: "1.5px solid #ddd", borderRadius: 8, fontSize: 14, marginBottom: 12, resize: "vertical", fontFamily: "inherit" }}
            />

            {err && <p style={{ color: "#c00", fontSize: 13, margin: "4px 0 10px" }}>{err}</p>}

            <Button
              type="submit" disabled={!!busy}
              style={{
                width: "100%", padding: "12px 16px", borderRadius: 10,
                background: "#25D366", color: "#fff", border: "none",
                fontWeight: 700, fontSize: 15, cursor: busy ? "wait" : "pointer",
                opacity: busy ? 0.7 : 1, marginBottom: 8,
              }}
            >
              {busy === "wa" ? t.sending : t.submitWa}
            </Button>

            {!bookingDetails && <Button
              type="button" onClick={submitEmail} disabled={!!busy}
              style={{
                width: "100%", padding: "12px 16px", borderRadius: 10,
                background: "#4698CB", color: "#fff", border: "none",
                fontWeight: 700, fontSize: 15, cursor: busy ? "wait" : "pointer",
                opacity: busy ? 0.7 : 1,
              }}
            >
              {busy === "email" ? t.sending : t.submitEmail}
            </Button>}

            <Button
              type="button" onClick={() => setOpen(false)} disabled={!!busy}
              style={{
                width: "100%", padding: "10px", marginTop: 6, background: "transparent",
                border: "none", color: "#666", fontSize: 13, cursor: "pointer",
              }}
            >
              {t.cancel}
            </Button>
          </form>
        </div>, document.body
      )}
    </>
  );
};

export default LeadCaptureCTA;
